/**
 * @file admin-dashboard.tsx
 * @module app/modules/dashboard/admin-dashboard
 * @description Renders the administrator dashboard with operational summaries and dashboard widgets.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 */

import { Button, Card, Col, OverlayTrigger, Row, Tooltip } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ShoppingBag,
  Banknote,
  Users,
  AlertTriangle,
  PackageCheck,
  Store,
  Package,
  CalendarClock,
} from 'lucide-react';
import ActiveSellersCustomers from './chart/active-sellers-customers';
import OrdersReport from './chart/orders-report';
import SalesReport from './chart/admin-sale-report';
import { useAuth } from '../auth';
import { useWarehouse } from '../../context/WarehouseContext';
import {
  AssistAdmin,
  Dashboard,
  FinanceManager,
  OrdersDelivery,
  View,
} from '../../../utils/constants';
import APICallService from '../../../api/apiCallService';
import { Fragment, useEffect, useState } from 'react';
import { AUTH, DASHBOARD } from '../../../api/apiEndPoints';
import DashboardReport from '../../modals/report/dashboard-report';
import Method from '../../../utils/methods';
import Loader from '../../../Global/loader';
import Notification from '../../notification/notification';
import TopNotifications from './top-notifications';
import PendingActions from '../inward/components/inward-pending-actions';
import ErrorIcon from '../../../_admin/assets/media/critical-errors.png';
import ExpiringProductsModal from './expiring-products';
import SummaryInfoCard from '../_common/components/summary-info-card';
import ImageWithFallback from '../_common/components/image-with-fallback';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  // Same roles TopNotifications shows for; otherwise Pending actions uses the full width
  const canSeeTopNotifications =
    currentUser?.roleAndPermission?.roleType == 'R0001' ||
    currentUser?.roleAndPermission?.roleType == 'R0003';
  // undefined while loading; the column only shows when there is at least one notification
  const [topNotificationCount, setTopNotificationCount] = useState<number | undefined>(undefined);
  const showTopNotifications = canSeeTopNotifications && (topNotificationCount ?? 0) > 0;
  // Opened from the overdue SLA bell: scroll to Pending actions and highlight it for a moment
  const location = useLocation();
  const [focusPending, setFocusPending] = useState(false);
  useEffect(() => {
    if ((location.state as { focus?: string } | null)?.focus !== 'pending-actions') return undefined;
    setFocusPending(true);
    const scroll = window.setTimeout(
      () => document.getElementById('pending-actions')?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
      100
    );
    const clear = window.setTimeout(() => setFocusPending(false), 4000);
    return () => {
      window.clearTimeout(scroll);
      window.clearTimeout(clear);
    };
  }, [location.key, location.state]);
  const { selectedWarehouse } = useWarehouse();
  const [loading, setLoading] = useState<boolean>(false);
  const [count, setCount] = useState({
    totalSellers: 0,
    totalActiveProducts: 0,
    totalCancelledOrders: 0,
    totalBuyers: 0,
    totalOrderAmount: 0,
    totalAppInstalled: [],
    vfdError: {
      error: 0,
      message: '',
    },
    todayTotalOrder: 0,
    totalOrdersDeliveredToday: 0,
    outOfStockProductCount: 0,
    listedProducts: 0,
    expiringProductCount: 0,
  });
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [notificationList, setNotificationList] = useState<any>([]);
  const [notificationLoader, setNotificationLoader] = useState(true);

  const [showExpiringProductsModal, setShowExpiringProductsModal] =
    useState(false);
  const selectedWarehouseId =
    selectedWarehouse?._id || selectedWarehouse?.value || '';
  // Fetch data whenever warehouse changes (including initial load)
  useEffect(() => {
    if (selectedWarehouse === null) return;
    (async () => {
      await fetchData(selectedWarehouseId);
      await fetchNotification();
    })();
  }, [selectedWarehouseId]);

  const fetchData = async (warehouse: any) => {
    let params: any = {
      needStats: true,
      needAllStats: true,
      fromDate: Method.convertDateToFormat(new Date(), 'YYYY-MM-DD'),
    };
    if (warehouse) {
      params.warehouse = warehouse;
    }
    setLoading(true);
    let apiService = new APICallService(
      DASHBOARD,
      params,
      '',
      '',
      '',
      '',
      Dashboard,
      warehouse
    );
    let response = await apiService.callAPI();
    if (response) {
      setCount(response.stats);
    }
    setLoading(false);
  };

  const fetchNotification = async () => {
    const params = {
      pageNo: 1,
      limit: 5,
      sortKey: '_createdAt',
      sortOrder: -1,
      type: 4,
    };
    setNotificationLoader(true);
    const apiCallService = new APICallService(
      AUTH.notification,
      params,
      '',
      '',
      '',
      ''
    );
    const response = await apiCallService.callAPI();
    if (response) {
      setNotificationList(response?.records || []);
    }
    setNotificationLoader(false);
  };

  return (
    <>
      {count.vfdError && count.vfdError.error ? (
        <>
          <Notification
            icon={false}
            bgColor="bg-danger"
            textColor="text-white"
            text={`${count.vfdError?.message}`}
            iconColor="text-warning"
            click={true}
            clickText={'click here'}
          />
        </>
      ) : (
        <></>
      )}
      <div className="p-5">
        <ExpiringProductsModal
          show={showExpiringProductsModal}
          onHide={() => setShowExpiringProductsModal(false)}
        />
        {showDownloadModal ? (
          <DashboardReport
            show={showDownloadModal}
            onHide={() => setShowDownloadModal(false)}
            moduleName={Dashboard}
          />
        ) : (
          <></>
        )}
        <Row className="mb-7 align-items-center">
          <Col xs className="fs-22 fw-bolder">
            Dashboard
          </Col>
          {!loading ? (
            <>
              {' '}
              {currentUser?.userType === AssistAdmin ? (
                <></>
              ) : (
                <>
                  {' '}
                  <Col sm="auto">
                    {Method.hasPermission(OrdersDelivery, View, currentUser) ? (
                      <Button
                        className="uw-btn uw-btn-primary"
                        data-analytics-event="dashboard_admin_dashboard_View orders & delivery plans_clicked"
                        onClick={() => {
                          navigate('/orders');
                        }}
                      >
                        View Delivery Plans
                      </Button>
                    ) : (
                      <></>
                    )}
                  </Col>
                </>
              )}
              {currentUser?.role.length === 0 ||
                currentUser?.role.some(
                  (obj: any) => obj.type === FinanceManager
                ) ? (
                <Col sm="auto">
                  <Button
                    className="uw-btn uw-btn-primary"
                    data-analytics-event="dashboard_admin_dashboard_download_report_clicked"
                    onClick={() => setShowDownloadModal(true)}
                  >
                    Download Report
                  </Button>
                </Col>
              ) : (
                <></>
              )}
            </>
          ) : (
            <></>
          )}
        </Row>
        {notificationList && notificationList?.length > 0 && (
          <Row className="mb-5 mt-4 ">
            <Col xs={12}>
              <Card className="border border-r10px">
                <Card.Header className="d-flex justify-content-between align-items-center">
                  <h2 className="fs-22 fw-bolder">Critical Alerts</h2>
                  {notificationList?.length > 0 && (
                    <div
                      className="fs-16 fw-500 text-primary cursor-pointer d-inline-block"
                      onClick={() =>
                        navigate('/notifications', { state: { type: 4 } })
                      }
                    >
                      View All
                    </div>
                  )}
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col xs={12}></Col>
                    <Col xs={12}>
                      {!notificationLoader ? (
                        notificationList?.length > 0 ? (
                          notificationList.map((item: any, index: number) => {
                            return (
                              <>
                                <div
                                  className="d-flex py-3 gap-8 fs-16 fw-500"
                                  onClick={() => {
                                    if (item?.metadata?.variantId) {
                                      navigate(
                                        '/products/master-products/view-product-details',
                                        {
                                          state:
                                            item?.metadata?.variantId || '',
                                        }
                                      );
                                    }
                                  }}
                                >
                                  <div>
                                    <ImageWithFallback
                                      src={
                                        item?.type == 4
                                          ? ErrorIcon
                                          : item?.image || ''
                                      }
                                      alt="icon"
                                      height={35}
                                      width={35}
                                    />
                                  </div>
                                  <div>
                                    <div>{item?.message}</div>
                                    <div className="text-gray-700">
                                      {Method.convertDateToDDMMYYYYHHMMAMPMWithSeperator(
                                        item?._createdAt,
                                        ' - '
                                      )}
                                    </div>
                                  </div>
                                </div>
                                {index !== notificationList.length - 1 && (
                                  <div className="separator my-5"></div>
                                )}
                              </>
                            );
                          })
                        ) : (
                          <div className="min-h-200px d-flex justify-content-center align-items-center">
                            <p className="fs-16 fw-500">No Alerts Found</p>
                          </div>
                        )
                      ) : (
                        <div className="min-h-200px d-flex justify-content-center align-items-center">
                          <Loader loading={notificationLoader} />
                        </div>
                      )}
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}
        {/* Half Top Notifications (super admin & finance role types), half Pending actions */}
        <Row className="g-7 mb-7 align-items-stretch">
          {canSeeTopNotifications && (
            // stays mounted to load; hidden while empty
            <Col lg={6} className={showTopNotifications ? '' : 'd-none'}>
              <TopNotifications onCount={setTopNotificationCount} />
            </Col>
          )}
          <Col
            lg={showTopNotifications ? 6 : 12}
            id="pending-actions"
            className={focusPending ? 'inward-pending-focus' : ''}
            style={{ scrollMarginTop: 90 }}
          >
            <PendingActions />
          </Col>
        </Row>
        <Row>
          <Col md={6}>
            {loading ? (
              <div className="w-100 d-flex justify-content-center">
                <Loader loading={loading} />
              </div>
            ) : (
              <Row className="align-items-center mb-5 g-6">
                <SummaryInfoCard
                  colKey="today-total-order"
                  bgClassName="bg-c9f3d7"
                  icon={<ShoppingBag />}
                  value={
                    Math.max(count.todayTotalOrder, 0)
                  }
                  label="Total Orders Today"
                  colProps={{ xl: 6, md: 2, xs: 2 }}
                />
                <SummaryInfoCard
                  colKey="total-orders-delivered-today"
                  bgClassName="bg-c9f3d7"
                  icon={<PackageCheck />}
                  value={
                    Math.max(count.totalOrdersDeliveredToday, 0)
                  }
                  label="Total Orders Delivered Today"
                  colProps={{ xl: 6, md: 2, xs: 2 }}
                />
                <OverlayTrigger
                  trigger={["hover", "focus"]}
                  placement="top"
                  overlay={
                    <Tooltip
                      id="order-value-tooltip"
                      className="tooltip-light tooltop-auto-width"
                    >
                      {`Today's total order value: TSh ${count.totalOrderAmount > 0
                        ? Method.getReadableAmount(count.totalOrderAmount)
                        : 0
                        }`}
                    </Tooltip>
                  }
                >
                  <SummaryInfoCard
                    colKey="today-total-order-value"
                    bgClassName="bg-fbe5e5"
                    icon={<Banknote />}
                    value={
                      <span
                      >
                        TSh{' '}
                        {count.totalOrderAmount > 0
                          ? Method.getGeneralizedAmount(count.totalOrderAmount)
                          : 0}
                      </span>
                    }
                    label="Today's total order value"
                    colProps={{ xl: 6, md: 2, xs: 2 }}
                  />
                </OverlayTrigger>
                <SummaryInfoCard
                  colKey="total-vendors"
                  bgClassName="bg-c9f3d7"
                  icon={<Store />}
                  value={Math.max(count.totalSellers, 0)}
                  label="Total vendors"
                  colProps={{ xl: 6, md: 2, xs: 2 }}
                />
                <SummaryInfoCard
                  colKey="total-customers"
                  bgClassName="bg-fbeace"
                  icon={<Users />}
                  value={Math.max(count.totalBuyers, 0)}
                  label="Total customers of UTrade"
                  colProps={{ xl: 6, md: 2, xs: 2 }}
                />
                <OverlayTrigger
                  trigger={["hover", "focus"]}
                  placement="top"
                  overlay={
                    <Tooltip
                      id="active-products-tooltip"
                      className="tooltip-light"
                      style={{ maxWidth: "260px" }}
                    >
                      <div>Total Active Product: {count.totalActiveProducts}</div>
                      <div>Total Listed Product (Variants): {count.listedProducts}</div>
                    </Tooltip>
                  }
                >
                  <SummaryInfoCard
                    colKey="active-products"
                    bgClassName="bg-c6e4fb"
                    icon={<Package />}
                    value={
                      <span
                      >
                        {Math.max(count.totalActiveProducts, 0)}
                      </span>
                    }
                    label="Active Products"
                    colProps={{ xl: 6, md: 2, xs: 2 }}
                  />
                </OverlayTrigger>
                <SummaryInfoCard
                  colKey="out-of-stock-products"
                  bgClassName="bg-fbe5e5"
                  icon={<AlertTriangle />}
                  value={
                    Math.max(count.outOfStockProductCount, 0)
                  }
                  label="Out of Stock Products"
                  colProps={{ xl: 6, md: 2, xs: 2 }}
                />
                <SummaryInfoCard
                  colKey="expiring-products"
                  bgClassName="bg-fff4d9"
                  icon={<CalendarClock />}
                  value={
                    Math.max(count.expiringProductCount, 0)
                  }
                  label="Expiring Products (in 1 Month)"
                  onClick={() => setShowExpiringProductsModal(true)}
                  colProps={{ xl: 6, md: 2, xs: 2 }}
                />
                {count.totalAppInstalled ? (
                  count.totalAppInstalled.map((val: any) => {
                    return (
                      <SummaryInfoCard
                        key={`app-installs-${val.platform}`}
                        bgClassName="bg-e3f4f8"
                        icon={<></>}
                        value={
                          Math.max(val.installedDevices, 0)
                        }
                        label={`${val.platform == 1 ? 'Android' : 'iOS'
                          } Installs`}
                        colProps={{ xl: 6, md: 2, xs: 2 }}
                      />
                    );
                  })
                ) : (
                  <></>
                )}
              </Row>
            )}
          </Col>
          <Col md={6}>
            <ActiveSellersCustomers />
          </Col>
        </Row>
        <SalesReport />
        <Row>
          <Col md={12}>
            <OrdersReport />
          </Col>
        </Row>
      </div>
    </>
  );
};
export default AdminDashboard;
